import { useUserStore } from '@/stores/user'
import { useUserMetricsStore } from '@/stores/userMetrics'
import { useModalsStore } from '@/stores/modals'
import { useConsultationStore } from '@/stores/consultation'
import { useCustomPromptLimits } from '@/composables/useCustomPromptLimits'
import { CUSTOM_PROMPTS_LIMITS } from '@/constants/resourceLimits'

/**
 * Guardas de paywall reutilizáveis para pontos de entrada do plano Free.
 */
export function usePaywallGuard() {
  const userStore = useUserStore()
  const userMetricsStore = useUserMetricsStore()
  const modalsStore = useModalsStore()
  const consultationStore = useConsultationStore()
  const customPromptLimits = useCustomPromptLimits()

  const { isProUser, isBillingBlocked, limits: userPlanLimits } =
    storeToRefs(userStore)
  const {
    isResourceUseLimitReached,
    resourceLimitModalType,
    exceededConsultationsLimit,
    exceededRecordingTimeLimit,
  } = storeToRefs(userMetricsStore)
  const { showResourceUseLimitModal } = storeToRefs(modalsStore)
  const { tabsConstructor } = storeToRefs(consultationStore)

  const {
    isCategoryLimitReached,
    canCreateNewCustomPrompt,
  } = customPromptLimits

  const openResourceLimitModal = (type) => {
    resourceLimitModalType.value = type
    showResourceUseLimitModal.value = true
  }

  const blockIfResourceLimitReached = () => {
    if (
      isBillingBlocked.value ||
      isProUser.value ||
      !isResourceUseLimitReached.value
    )
      return false

    if (exceededConsultationsLimit.value) {
      openResourceLimitModal('consultations')
    } else if (exceededRecordingTimeLimit.value) {
      openResourceLimitModal('recordingTime')
    }

    return true
  }

  const blockIfCustomPromptCreationNotAllowed = () => {
    if (isBillingBlocked.value || isProUser.value) return false

    if (!canCreateNewCustomPrompt.value) {
      openResourceLimitModal('customPrompts')
      return true
    }

    return false
  }

  const blockIfAllCustomPromptsLimitReached = blockIfCustomPromptCreationNotAllowed

  const blockIfCustomPromptCategoryLimitReached = (category) => {
    if (isBillingBlocked.value || isProUser.value || !category) return false

    if (isCategoryLimitReached(category)) {
      openResourceLimitModal('customPrompts')
      return true
    }

    return false
  }

  const getOutputTabsCount = () => {
    return (tabsConstructor.value || []).filter((tab) => tab.id !== 'exams')
      .length
  }

  const blockIfMaxTabsLimitReached = () => {
    if (isBillingBlocked.value || isProUser.value) return false

    const tabsLimit =
      userPlanLimits.value?.outputTabsPerConsultation ??
      CUSTOM_PROMPTS_LIMITS.tabs

    if (getOutputTabsCount() < tabsLimit) return false

    openResourceLimitModal('maxTabsLimit')
    return true
  }

  return {
    blockIfResourceLimitReached,
    blockIfConsultationLimitReached: blockIfResourceLimitReached,
    blockIfCustomPromptCreationNotAllowed,
    blockIfAllCustomPromptsLimitReached,
    blockIfCustomPromptCategoryLimitReached,
    blockIfMaxTabsLimitReached,
    customPromptLimits,
  }
}
